import {
  compactOpenerThemeForKind,
  DEFAULT_OPENER_KIND,
  type OpenerColorRole,
  resolveShopifyOpenerTheme,
  SAMPLE_OPENER_BOX,
  type ShopifyOpenerThemeDraft,
  shopifyOpenerThemeDraftOf,
} from "@loot-os/api-contract";
import { LootBoxOpener, resolveTheme } from "@loot-os/react";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { previewOpenerClient } from "../lib/previewOpenerClient";
import { useTRPC } from "../lib/trpc";
import { ErrorAlert } from "./ErrorAlert";
import { OpenerStyleSelect } from "./OpenerStyleSelect";
import { OpenerThemeEditor } from "./OpenerThemeEditor";

/** The opener's stock palette, which an unset color keeps. */
const STOCK_COLORS = resolveTheme().colors;

/** A Shopify shop prices in USD, so the overlay's opener shows dollars. */
const SHOPIFY_CURRENCY = {
  name: "dollar",
  namePlural: "dollars",
  symbol: "$",
  multiplier: 100 as const,
  iconSvg: "",
} as const;

/**
 * Shopify → Appearance: the opener's colors in the shop's storefront overlay (those of the
 * animation the shop's style opens with, then the color roles, corner radius, font and rarity
 * colors) in the shared `OpenerThemeEditor`, with the real opener spinning one of the shop's boxes
 * (a sample box while it sells none) in demo mode, opened with the shop's opener style. The
 * merchant edits the same theme in the embedded admin (Appearance); an unset accent follows the
 * shop's brand color. Admins save; others read.
 */
export function ShopifyOpenerAppearance({ canEdit }: { canEdit: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const appearance = useQuery(trpc.shopify.openerAppearance.queryOptions());
  const [draft, setDraft] = useState<ShopifyOpenerThemeDraft | null>(null);
  const save = useMutation(
    trpc.shopify.setOpenerTheme.mutationOptions({
      onSuccess: () => {
        toast.success("Opener theme saved.");
        setDraft(null);
        void queryClient.invalidateQueries({
          queryKey: trpc.shopify.openerAppearance.queryKey(),
        });
      },
    }),
  );

  const saveStyle = useMutation(
    trpc.shopify.setOpenerStyle.mutationOptions({
      onSuccess: () => {
        toast.success("Opener style saved.");
        void queryClient.invalidateQueries({
          queryKey: trpc.shopify.openerAppearance.queryKey(),
        });
      },
    }),
  );

  const data = appearance.data;
  const stored = data?.theme ?? null;
  const brandColor = data?.brandColor ?? null;
  // The animation boxes that name no style open with: the shop's style, else the one it inherits.
  const shopStyleId = data?.openerStyleId ?? data?.inheritedOpenerStyleId;
  const kind =
    data?.openerStyles.find((style) => style.id === shopStyleId)?.kind ?? DEFAULT_OPENER_KIND;
  const form = draft ?? shopifyOpenerThemeDraftOf(stored);
  const theme = compactOpenerThemeForKind(form, kind);
  // The compacted theme is rebuilt every render; its JSON is what changes.
  const themeKey = JSON.stringify(theme);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized theme
  const openerTheme = useMemo(
    () => resolveShopifyOpenerTheme(theme, brandColor),
    [themeKey, brandColor],
  );
  const shopBox = data?.previewBox ?? null;
  const previewBox = shopBox ?? SAMPLE_OPENER_BOX;
  const createClient = useMemo(() => previewOpenerClient(previewBox), [previewBox]);

  if (appearance.error) return <ErrorAlert message={appearance.error.message} />;
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  // An unset accent shows what the shopper gets instead: the brand color, when it reads.
  const accentFallback = resolveShopifyOpenerTheme(null, brandColor)?.colors;
  const fallbackFor = (role: OpenerColorRole) =>
    (role === "accent" || role === "accentForeground" ? accentFallback?.[role] : undefined) ??
    STOCK_COLORS[role];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate({ theme });
      }}
    >
      <OpenerThemeEditor
        idPrefix="opener"
        kind={kind}
        draft={form}
        onChange={setDraft}
        fallbackFor={fallbackFor}
        fontPlaceholder="The shop's own font"
        disabled={!canEdit}
        aside={
          <Card>
            <CardHeader>
              <CardTitle>Overlay</CardTitle>
              <CardDescription>
                How the opener looks in the shop's storefront overlay. Blank fields keep the
                opener's own palette; a blank button color follows the shop's brand color
                {brandColor ? ` (${brandColor})` : ""}. The merchant edits the same theme under
                Appearance in the Shopify app.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4">
              <OpenerStyleSelect
                id="shop-opener-style"
                label="Opener style"
                description="The animation, and any colors it sets over the shop's, for boxes that name no style of their own."
                styles={data.openerStyles}
                value={data.openerStyleId}
                inheritLabel="Same as the environment"
                inheritedStyleId={data.inheritedOpenerStyleId}
                disabled={!canEdit || saveStyle.isPending}
                onChange={(styleId) => saveStyle.mutate({ styleId })}
              />
              {canEdit && (
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={save.isPending || draft === null}>
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={save.isPending || draft === null}
                    onClick={() => setDraft(null)}
                  >
                    Discard changes
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={save.isPending || theme === null}
                    // Clears the form to the brand color; Save confirms it.
                    onClick={() =>
                      setDraft(stored === null ? null : shopifyOpenerThemeDraftOf(null))
                    }
                  >
                    Reset to brand color
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        }
        preview={
          <div className="grid min-w-0 content-start gap-2">
            <LootBoxOpener
              key={previewBox.id}
              boxId={previewBox.id}
              createClient={createClient}
              // Non-empty so the SDK calls the factory; the factory answers locally.
              publishableKey="preview"
              sessionToken={null}
              theme={openerTheme}
              opener={data.opener}
              maxQuantity={1}
              defaultQuantity={1}
              currency={SHOPIFY_CURRENCY}
              sounds={false}
              showFairness={false}
            />
            {shopBox === null && (
              <p className="text-sm text-muted-foreground">
                A sample box. Once a published box is for sale on Shopify, the preview spins it.
              </p>
            )}
          </div>
        }
      />
    </form>
  );
}
